import { Button } from '../../components/controls.js'
import { Dialog } from '../../components/Dialog.js'
import { ACCENTS, DARK_SURFACES, LIGHT_SURFACES, useThemeStore } from '../../state/theme-store.js'
import { useUiStore } from '../../state/ui-store.js'
import styles from './AppearanceDialog.module.css'

/**
 * Every control applies immediately — there is nothing to save, so "Done" only closes.
 * Preferences live in localStorage, not on the board, so they never enter undo history.
 */
export function AppearanceDialog() {
  const open = useUiStore((state) => state.appearanceOpen)
  const setAppearanceOpen = useUiStore((state) => state.setAppearanceOpen)

  const mode = useThemeStore((state) => state.mode)
  const lightSurface = useThemeStore((state) => state.lightSurface)
  const darkSurface = useThemeStore((state) => state.darkSurface)
  const accent = useThemeStore((state) => state.accent)
  const setMode = useThemeStore((state) => state.setMode)
  const setLightSurface = useThemeStore((state) => state.setLightSurface)
  const setDarkSurface = useThemeStore((state) => state.setDarkSurface)
  const setAccent = useThemeStore((state) => state.setAccent)

  const close = () => setAppearanceOpen(false)

  // Only the live mode's surfaces are offered; the other mode keeps its own saved choice.
  const surfaces = mode === 'light' ? LIGHT_SURFACES : DARK_SURFACES
  const activeSurface: string = mode === 'light' ? lightSurface : darkSurface
  const chooseSurface = (key: string) => {
    if (mode === 'light') setLightSurface(key as typeof lightSurface)
    else setDarkSurface(key as typeof darkSurface)
  }

  return (
    <Dialog
      open={open}
      title="Appearance"
      onClose={close}
      onSubmit={close}
      wide
      footer={
        <Button variant="primary" onClick={close} data-autofocus>
          Done
        </Button>
      }
    >
      <p className={styles.intro}>
        Choose separate surfaces for light and dark mode. Preferences are saved in this browser.
      </p>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Mode</h3>
        <div className={styles.modeGroup} role="group" aria-label="Colour mode">
          <button
            type="button"
            className={styles.modeButton}
            aria-pressed={mode === 'light'}
            onClick={() => setMode('light')}
          >
            Light
          </button>
          <button
            type="button"
            className={styles.modeButton}
            aria-pressed={mode === 'dark'}
            onClick={() => setMode('dark')}
          >
            Dark
          </button>
        </div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{mode === 'light' ? 'Light surface' : 'Dark surface'}</h3>
        <div className={styles.surfaceGrid} role="group" aria-label={`${mode === 'light' ? 'Light' : 'Dark'} surface`}>
          {surfaces.map((surface) => (
            <button
              key={surface.key}
              type="button"
              className={styles.surfaceCard}
              aria-pressed={activeSurface === surface.key}
              onClick={() => chooseSurface(surface.key)}
            >
              <span className={styles.swatchRow} aria-hidden="true">
                {surface.swatches.map((colour) => (
                  <span key={colour} className={styles.swatch} style={{ background: colour }} />
                ))}
              </span>
              <span className={styles.surfaceLabel}>{surface.label}</span>
              <span className={styles.surfaceDescription}>{surface.description}</span>
            </button>
          ))}
        </div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Accent colour</h3>
        <div className={styles.accentRow} role="group" aria-label="Accent colour">
          {ACCENTS.map((option) => (
            <button
              key={option.key}
              type="button"
              className={styles.accentButton}
              aria-pressed={accent === option.key}
              aria-label={option.label}
              title={option.label}
              onClick={() => setAccent(option.key)}
            >
              <span className={styles.accentDisc} style={{ background: option.swatch[mode] }}>
                <svg
                  className={styles.check}
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="m5 12.5 4.5 4.5L19 7.5" />
                </svg>
              </span>
            </button>
          ))}
        </div>
      </section>
    </Dialog>
  )
}
